import { decodeHtmlEntities } from '../utils/html'

/**
 * Collection decoupled → vocabulaire d'une facette Elasticsearch.
 *
 * GÉNÉRIQUE et piloté par l'URL : le processor ne connaît ni entité, ni domaine.
 * Ce qu'il faut lire et comment fabriquer la valeur est déclaré dans le YAML
 * (donc du CONTENU), sur le modèle du marqueur `rawSyncable=1` déjà en place
 * dans [decoupledApiProcessors.js](./decoupledApiProcessors.js) :
 *
 *   /decoupled-api/{entity}/{bundle}/collection?display=decoupled&asFacet=1
 *     &facetValue=id        # champ portant la valeur   (défaut: id)
 *     &facetLabel=name      # champ portant le libellé  (défaut: name)
 *     &facetPrefix=group:   # préfixe de la valeur       (défaut: aucun)
 *     &facetHumanize=1      # humanise les libellés CONSTANTE (défaut: non)
 *     &facetParent=parent   # champ portant le parent    (défaut: aucun)
 *     &facetOmitRoots=1     # exclut des choix les termes PARENTS (exige facetParent)
 *
 * Rend les quatre formes dont une facette a besoin, plus `ok` :
 *
 *   { values: ["group:5"],                       // pré-cocher (FacetList.dataLocation)
 *     labels: { "group:5": "EA Lab" },           // étiqueter les buckets (labelMap)
 *     options: [{ value, label }],               // liste de choix (FuzzySelectField…)
 *     ok: true,                                  // false : erreur ou réponse illisible
 *     buckets: [{ key: "group:5" }] }            // vocabulaire d'une FacetList
 *
 * Avec `facetParent`, deux tables de plus — une taxonomie HIÉRARCHIQUE rend ainsi
 * sa sur-catégorie affichable partout où l'écran n'a que l'id sous la main
 * (cellule de tableau, champ de la fiche, chip de facette), sans second appel :
 *
 *   { parents:      { "43": "303" },                        // id → id du parent
 *     parentLabels: { "43": "Marketing, media & creative" } } // id → libellé du parent
 *
 * `buckets` est la MÊME liste que `options`, à la forme d'une agrégation
 * Elasticsearch (`[{key}]`) : c'est ce que `FacetList` sait lire. Sans elle, une
 * facette dont le vocabulaire est une collection decoupled (et non un index ES)
 * n'aurait aucun moyen déclaratif d'être alimentée. Pas de `doc_count` : rien ici
 * ne compte de documents — la facette se rend avec `showCount: false`.
 *
 * OPT-IN STRICT : sans `asFacet=1` la réponse ressort INTACTE. Contrairement aux
 * processors qui s'accrochent à un chemin d'URL (et finissent avec une liste
 * d'exceptions, cf. `taxonomyToOptionsProcessor`), celui-ci ne détourne jamais
 * la réponse d'un écran qui ne l'a pas demandé.
 *
 * POURQUOI DU JS (justification config-first) : une facette filtre sur la valeur
 * INDEXÉE, qui n'est presque jamais l'identifiant brut de l'entité — sur
 * /crm/deals le champ `es_group_grants` porte `group:5` quand decoupled_toolbox
 * rend `{id: 5}`. Or reactive-json ne sait pas transformer une donnée :
 * `simpleMapping` DÉPLACE une valeur, `collectField` est une action qui exige un
 * événement, et l'interpolation ne concatène que dans une URL. Sans ce
 * ré-étiquetage une facette ne peut donc être ni pré-cochée ni lisible. C'est une
 * NORMALISATION de réponse — la raison d'être du type `dataProcessor` — et elle
 * est ici écrite UNE fois pour toutes les facettes, pas par cas d'usage.
 *
 * Enregistré dans [App.jsx](../App.jsx) sous `dataProcessor['collection-to-facet']`.
 */
export const COLLECTION_TO_FACET_PARAM = 'asFacet=1'

/**
 * Une CONSTANTE de nomenclature : majuscules, chiffres et `_`, rien d'autre.
 *
 * C'est la forme des enums d'un export (HubSpot rend `MARKETING_AND_ADVERTISING`),
 * et c'est aussi ce qui permet de n'humaniser QUE celles-là : un libellé déjà
 * rédigé (`Food & Beverage`, `Production d'électricité`) ne matche pas et ressort
 * mot pour mot, donc `facetHumanize=1` ne peut pas abîmer un vocabulaire mixte.
 */
const CONSTANT_LABEL = /^[A-Z0-9][A-Z0-9_]*$/

/**
 * `MARKETING_AND_ADVERTISING` → `Marketing and advertising`.
 *
 * Purement mécanique (souligné → espace, puis casse de phrase) : aucune liste
 * d'exceptions à maintenir, donc aucun libellé ne peut se retrouver orphelin
 * quand la source en ajoute un. Le nom du terme, lui, reste l'enum brut — c'est
 * la clé de jointure avec le système source, et la renommer ferait recréer des
 * doublons au prochain import (cf. aodb_import/scripts/group-industries.php).
 */
export const humanizeConstant = (label) => {
  const text = String(label ?? '')
  if (!CONSTANT_LABEL.test(text)) return text
  const words = text.replace(/_+/g, ' ').trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const collectionToFacetProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  const url = String(requestContext?.url ?? '')
  if (!url.includes(COLLECTION_TO_FACET_PARAM)) return dataToProcess

  // Les URLs sont relatives : une base bidon suffit à réutiliser le parseur natif.
  const params = new URL(url, 'http://localhost').searchParams

  // `ok` distingue « collection lue, peut-être vide » de « lecture ratée » : un
  // écran qui exige un choix dans `options` s'y fie pour ne pas prendre une
  // erreur pour une liste vide. La forme vide porte les MÊMES clés que la forme
  // pleine : un écran qui lit `parentLabels` ne doit pas voir la clé disparaître
  // selon l'issue de la requête.
  const empty = { values: [], labels: {}, options: [], buckets: [], ok: false }
  if (params.get('facetParent')) {
    empty.parents = {}
    empty.parentLabels = {}
  }
  // Sur erreur on rend la forme vide : la page reste utilisable, simplement sans
  // pré-sélection ni libellés (jamais de facette pré-cochée « au hasard »).
  if (responseContext?.status >= 400) return empty
  if (!Array.isArray(dataToProcess)) return empty

  const valueKey = params.get('facetValue') || 'id'
  const labelKey = params.get('facetLabel') || 'name'
  const prefix = params.get('facetPrefix') || ''
  const humanize = params.get('facetHumanize') === '1'
  const parentKey = params.get('facetParent') || ''
  const omitRoots = params.get('facetOmitRoots') === '1' && parentKey !== ''

  const values = []
  const labels = {}
  const options = []
  const buckets = []
  // Renseignées seulement quand `facetParent` est déclaré : sans lui la réponse
  // garde EXACTEMENT sa forme d'avant, pour les écrans déjà câblés.
  const parents = {}
  const parentLabels = {}
  // 1er passage : [valeur, valeur du parent] pour chaque ligne lue. Les libellés
  // et le statut « ce terme est un regroupement » ne sont résolus qu'ensuite — un
  // parent peut arriver APRÈS son enfant dans la collection.
  const rows = []

  for (const item of dataToProcess) {
    // Collection de Syncables ({item_url, data}) ou objets à plat selon le display.
    const record = item?.data ?? item
    const raw = record?.[valueKey]
    if (raw === undefined || raw === null || raw === '') continue
    const value = `${prefix}${raw}`
    if (Object.prototype.hasOwnProperty.call(labels, value)) continue
    const decoded = decodeHtmlEntities(String(record[labelKey] ?? ''))
    const label = (humanize ? humanizeConstant(decoded) : decoded) || value

    // Un champ entity-reference sort en TABLEAU d'ids (`parent: ["303"]`), une
    // racine en `null` : les deux formes se ramènent ici à un id ou rien.
    let parentRaw = ''
    if (parentKey) {
      const ref = record[parentKey]
      const first = Array.isArray(ref) ? ref[0] : ref
      // Drupal stocke 0 pour « pas de parent » — un id, mais pas un terme.
      parentRaw = (first === undefined || first === null || `${first}` === '0') ? '' : `${first}`
    }
    labels[value] = label
    rows.push([value, parentRaw ? `${prefix}${parentRaw}` : ''])
  }

  // `facetOmitRoots` écarte des choix les termes qui SONT le parent d'un autre —
  // et NON ceux qui n'ont pas de parent. La nuance est ce qui évite de faire
  // disparaître un terme légitimement seul à son niveau : dans `company_industry`,
  // tout enum qu'un import futur créerait arrive à plat, sans sur-catégorie, tant
  // que la carte d'AODB ne le range pas. Les compter comme des regroupements les
  // rendrait INCHOISISSABLES, sans le moindre signal.
  const groupValues = new Set(
    omitRoots ? rows.map(([, parentValue]) => parentValue).filter(Boolean) : [],
  )

  for (const [value] of rows) {
    if (groupValues.has(value)) continue
    values.push(value)
    options.push({ value, label: labels[value] })
    buckets.push({ key: value })
  }

  if (parentKey) {
    for (const [value, parentValue] of rows) {
      if (!parentValue) continue
      parents[value] = parentValue
      // Un parent hors collection (filtrée, ou droit d'accès) n'invente rien :
      // la clé reste absente, et `MapText` retombe sur son `default`.
      if (Object.prototype.hasOwnProperty.call(labels, parentValue)) {
        parentLabels[value] = labels[parentValue]
      }
    }
    return { values, labels, options, buckets, parents, parentLabels, ok: true }
  }

  return { values, labels, options, buckets, ok: true }
}
